// Global keyboard map for the Zen app. Ignored while typing in a field or when a dialog is open.
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasCapability } from './access';
import { useZenBootstrap } from './hooks';

export const SHORTCUTS: Array<{ keys: string[]; label: string }> = [
  { keys: ['⌘', 'K'], label: 'Command palette' },
  { keys: ['/'], label: 'Focus list search' },
  { keys: ['N'], label: 'New work order' },
  { keys: ['G', 'O'], label: 'Go to work orders' },
  { keys: ['G', 'Q'], label: 'Go to my queue' },
  { keys: ['G', 'D'], label: 'Go to dashboard' },
  { keys: ['Enter'], label: 'Open the selected row' },
  { keys: ['?'], label: 'This shortcut sheet' },
];

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function useGlobalShortcuts({ openPalette, openSheet }: { openPalette: () => void; openSheet: () => void }) {
  const navigate = useNavigate();
  const { data } = useZenBootstrap();
  const pendingG = useRef(0);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openPalette();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      if (document.querySelector('.ant-modal-wrap:not([style*="display: none"]) .ant-modal')) return;
      const key = event.key;
      const now = Date.now();
      if (now - pendingG.current < 1_000) {
        pendingG.current = 0;
        const target = { o: '/work-orders', q: '/queue', d: '/dashboard' }[key.toLowerCase()];
        if (target) {
          event.preventDefault();
          navigate(target);
        }
        return;
      }
      if (key === 'g' || key === 'G') {
        pendingG.current = now;
        return;
      }
      if (key === '?') {
        event.preventDefault();
        openSheet();
      } else if (key === '/') {
        const search = document.querySelector<HTMLInputElement>('[data-shortcut-search] input, input[data-shortcut-search]');
        if (search) {
          event.preventDefault();
          search.focus();
        }
      } else if ((key === 'n' || key === 'N') && data && hasCapability(data.currentUser, data.departments, 'create-order')) {
        event.preventDefault();
        navigate('/work-orders/new');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, navigate, openPalette, openSheet]);
}
